import React, { FC, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { object, string } from 'yup';
import { ApiErrorCont, Form, Link, InputCont, MicrosoftButton } from './SignInForm.styles';
import { RouteNames } from '../../constans/routes';
import { getMicrosoftLoginUrl, ILoginRequest } from '../../services/auth';
import Input from '../Input/Input';
import Notification from '../Notification/Notification';
import SubmitButton from '../SubmitButton/SubmitButton';

interface ISignInForm {
  submitForm: (data: ILoginRequest) => void;
  apiError?: {
    title: string | null;
    description: string | null;
  };
}
const SignInForm: FC<ISignInForm> = ({ submitForm, apiError }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const signInSchema = object({
    email: string()
      .email(t('login.signIn.emailMustBeAValidEmail'))
      .required(t('login.signIn.emailIsRequired')),
    password: string().required(t('login.signIn.passwordIsRequired')),
  });
  const formMethods = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(signInSchema),
  });
  const { handleSubmit } = formMethods;

  const resetPassword = useCallback(() => navigate(RouteNames.PasswordReset), [navigate]);

  const handleMicrosoftLoginClick = async () => {
    try {
      const redirectUri = window.location.origin;
      const nonce = Math.random().toString(36).substring(7);

      const loginUrl = await getMicrosoftLoginUrl(redirectUri, nonce);

      window.location.href = loginUrl;
    } catch (error) {
      console.error('Failed to get Microsoft login URL:', error);
    }
  };

  const message = {
    mode: 'error',
    type: 'static',
    title: apiError?.title || 'error',
    description: apiError?.description || 'error',
  };

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(submitForm)}>
        <InputCont>
          <Input
            label={t('login.signIn.email')}
            registerPath={'email'}
            placeholder={t('login.signIn.yourEmailAddress')}
          />
          <Input
            label={t('login.signIn.password')}
            registerPath={'password'}
            placeholder={t('login.signIn.yourPassword')}
            type="password"
          />
          <Link data-testid="forgot-password-link" onClick={resetPassword}>
            {t('login.signIn.forgotPassword')}
          </Link>
        </InputCont>
        {apiError?.description && (
          <ApiErrorCont>
            <Notification data={message} />
          </ApiErrorCont>
        )}
        <SubmitButton text={t('login.signIn.login')} />
        <MicrosoftButton type="button" onClick={handleMicrosoftLoginClick}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
            <rect x="1" y="1" width="9" height="9" fill="#f25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
            <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
          </svg>
          {t('login.signIn.loginWithMicrosoft')}
        </MicrosoftButton>
      </Form>
    </FormProvider>
  );
};

export default SignInForm;
